import { useEffect, useState } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body2Skeleton } from '../../Skeletons/Skeletons';
import { FaUserCheck, FaMobileAlt, FaLeaf, FaCalendarAlt, FaUtensils, FaMapMarkerAlt, FaPercent, FaUndo, FaGift } from 'react-icons/fa';
import DownloadAppModal from '../../Modals/DownloadAppModal';

// Default card styles (icons and colors)
const cardStyles = [
  { icon: FaUserCheck, bgColor: "bg-orange-100", iconColor: "text-orange-500" },
  { icon: FaMobileAlt, bgColor: "bg-blue-100", iconColor: "text-blue-500" },
  { icon: FaLeaf, bgColor: "bg-green-100", iconColor: "text-green-500" },
  { icon: FaCalendarAlt, bgColor: "bg-purple-100", iconColor: "text-purple-500" },
  { icon: FaUtensils, bgColor: "bg-amber-100", iconColor: "text-amber-500" },
  { icon: FaMapMarkerAlt, bgColor: "bg-red-100", iconColor: "text-red-400" },
];

// Default offer bar icons
const offerIcons = [FaPercent, FaUndo, FaGift];

const Body2 = () => {
  const { data: homeData, isOutsideProvider } = useHomeData();
  const [localData, setLocalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fallback fetch for when used outside HomeDataProvider
  useEffect(() => {
    if (!isOutsideProvider) return;
    const fetchBody2Data = async () => {
      try {
        const [offersSnap, bigImageSnap, mainHeadingSnap, subHeadingSnap, buttonSnap, cardsSnap] =
          await Promise.all([
            getDocs(collection(db, 'home', 'offerBar', 'offer1')),
            getDoc(doc(db, 'home', 'body2', 'bigImage', 'image')),
            getDoc(doc(db, 'home', 'body2', 'mainHeading', 'mainHeading')),
            getDoc(doc(db, 'home', 'body2', 'mainHeading', 'subHeading')),
            getDoc(doc(db, 'home', 'body2', 'button', 'button')),
            getDocs(collection(db, 'home', 'body2', 'cards')),
          ]);
        const offers = [];
        offersSnap.forEach((d) => offers.push({ id: d.id, ...d.data() }));
        offers.sort((a, b) => a.id.localeCompare(b.id));
        const cards = [];
        cardsSnap.forEach((d) => cards.push({ id: d.id, ...d.data() }));
        cards.sort((a, b) => a.id.localeCompare(b.id));
        setLocalData({
          offers,
          bigImage: bigImageSnap.exists() ? bigImageSnap.data() : null,
          heading: mainHeadingSnap.exists() ? mainHeadingSnap.data() : {},
          subHeading: subHeadingSnap.exists() ? (subHeadingSnap.data().text || '') : '',
          buttonText: buttonSnap.exists() ? (buttonSnap.data().text || 'Start Your Food Journey') : 'Start Your Food Journey',
          cards,
        });
      } catch (error) {
        console.error('Error fetching body2 data:', error);
      }
    };
    fetchBody2Data();
  }, [isOutsideProvider]);

  const body2Data = isOutsideProvider ? localData : homeData?.body2;

  if (!body2Data) {
    return <Body2Skeleton />;
  }

  // Merge Firestore card data with default styles
  const displayCards = body2Data.cards.length > 0
    ? body2Data.cards.map((card, index) => ({
      ...cardStyles[index] || cardStyles[0],
      title: card.h1,
      description: card.h2,
      img: card.img || null,
      alt: card.alt || "",
      titleText: card.title || "",
    }))
    : [];

  // Process offers
  const displayOffers = body2Data.offers.length > 0
    ? body2Data.offers.map((offer, index) => ({
      icon: offerIcons[index] || offerIcons[0],
      heading: offer.heading,
      subheading: offer.subHeading,
    }))
    : [];


  return (
    <>
      {/* Offer Bar */}
      {displayOffers.length > 0 && (
        <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 py-3 sm:py-4 shadow-inner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-6 sm:gap-12 lg:gap-36 pl-12 pr-4 sm:px-0">
              {displayOffers.map((offer, index) => {
                const IconComponent = offer.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 text-white"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-sm shadow-[0_4px_12px_rgba(0,0,0,0.1)] flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-lg sm:text-xl" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-sm sm:text-base">{offer.heading}</p>
                      <p className="text-xs sm:text-sm opacity-90">{offer.subheading}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <section className="bg-white py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32">
        {/* Heading */}
        <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            {body2Data.heading.black || ''}{" "}
            <span className="text-orange-500">{body2Data.heading.orange || ''}</span>{" "}
            {body2Data.heading.black2 || ''}
          </h2>
          <div className="w-20 sm:w-24 h-1 bg-orange-500 mx-auto mt-6"></div>
          {body2Data.subHeading && (
            <p className="text-gray-600 mt-4 text-base sm:text-lg">{body2Data.subHeading}</p>
          )}
        </div>

        {/* Main Content: Cards Left, Image Right */}
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch mt-10 sm:mt-14">
          {/* Feature Cards - Left Side */}
          {displayCards.length > 0 && (
            <div className="w-full lg:w-1/2">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 h-full">
                {displayCards.map((card, index) => {
                  const IconComponent = card.icon;
                  return (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-2xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col items-start text-left"
                    >
                      {/* Icon */}
                      <div className="-ml-1 sm:-ml-1.5 mb-2 sm:mb-3">
                        {card.img ? (
                          <img
                            src={card.img}
                            alt={card.alt || card.title}
                            title={card.titleText || card.title}
                            width={56}
                            height={56}
                            loading="lazy"
                            decoding="async"
                            className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                          />
                        ) : (
                          <IconComponent className={`text-4xl sm:text-5xl ${card.iconColor}`} />
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1.5 leading-tight">
                        {card.title}
                      </h3>

                      {/* Description */}
                      <p className="text-sm sm:text-base text-gray-500 leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Large Image - Right Side */}
          <div className="w-full lg:w-1/2 flex items-center justify-center relative px-4 lg:px-0 mt-8 lg:mt-0">
            {/* Decorative rotated background block */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-orange-50 rounded-[2.5rem] transform rotate-3 scale-105 -z-10 transition-transform duration-500 hover:rotate-6"></div>
            
            {body2Data.bigImage ? (
              <img
                src={body2Data.bigImage.image}
                alt={body2Data.bigImage.alt || "Delicious Home Cooked Food Spread by BentoBox"}
                title={body2Data.bigImage.title || "BentoBox Home Cooked Meals"}
                width={600}
                height={400}
                loading="lazy"
                decoding="async"
                className="w-full h-full max-h-[500px] lg:max-h-[600px] rounded-3xl shadow-xl object-cover relative z-10 border-4 border-white"
              />
            ) : (
              <div className="w-full h-full min-h-[400px] bg-gray-100 rounded-3xl shadow-xl flex items-center justify-center relative z-10 border-4 border-white">
                <p className="text-gray-400 font-medium text-center">Image will be displayed here</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center mt-12 sm:mt-16">
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-200 hover:shadow-lg hover:shadow-orange-200"
          >
            {body2Data.buttonText}
          </button>
        </div>
      </section>

      <DownloadAppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default Body2;